import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ScatterChart, Scatter
} from 'recharts';
import { VictoryChart, VictoryLine, VictoryBar, VictoryArea, VictoryPie, VictoryScatter, VictoryTheme, VictoryAxis, VictoryTooltip } from 'victory';
import { ChartConfig } from '../../api/aiDashboardApi';
import { TrendingUp, BarChart3, PieChart as PieChartIcon, Activity, Zap } from 'lucide-react';

interface AdvancedChartRendererProps {
  config: ChartConfig;
  library?: 'recharts' | 'victory' | 'chartjs';
  interactive?: boolean;
  animated?: boolean;
}

export const AdvancedChartRenderer: React.FC<AdvancedChartRendererProps> = ({ 
  config, 
  library = 'recharts',
  animated = true 
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Enhanced color palettes
  const colorPalettes = {
    modern: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'],
    gradient: ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'],
    professional: ['#1f2937', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6', '#f9fafb'],
    vibrant: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd', '#98d8c8', '#f7dc6f']
  };

  const colors = colorPalettes.modern;

  // Transform data for different chart libraries
  const transformedData = useMemo(() => {
    return config.data.map((item, index) => ({
      ...item,
      name: item.label || item.name || `Item ${index + 1}`,
      value: Number(item.value || item.count || 0),
      fill: colors[index % colors.length],
      color: colors[index % colors.length]
    }));
  }, [config.data]);

  // Calculate statistics
  const stats = useMemo(() => {
    const values = transformedData.map(d => d.value);
    const total = values.reduce((sum, val) => sum + val, 0);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const avg = total / values.length;
    
    return { total, max, min, avg, count: values.length };
  }, [transformedData]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-800">{label}</p>
          <p className="text-blue-600">
            <span className="font-medium">Value: </span>
            {data.value?.toLocaleString()}
          </p>
          {data.payload.percentage && (
            <p className="text-gray-600">
              <span className="font-medium">Percentage: </span>
              {data.payload.percentage}%
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Recharts implementation
  const renderRechartsChart = () => {
    const commonProps = {
      data: transformedData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    };

    switch (config.type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="value" 
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {transformedData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={activeIndex === index ? '#1D4ED8' : entry.fill}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#3B82F6"
                strokeWidth={3}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, stroke: '#1D4ED8', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart {...commonProps}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke="#3B82F6"
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorValue)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        const dataWithPercentage = transformedData.map(item => ({
          ...item,
          percentage: ((item.value / stats.total) * 100).toFixed(1)
        }));

        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={dataWithPercentage}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percentage }) => `${name}: ${percentage}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {dataWithPercentage.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.fill}
                    stroke={activeIndex === index ? '#1D4ED8' : 'none'}
                    strokeWidth={activeIndex === index ? 3 : 0}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis dataKey="value" tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Scatter dataKey="value" fill="#3B82F6" />
            </ScatterChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="value" 
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {transformedData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={activeIndex === index ? '#1D4ED8' : entry.fill}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  // Victory charts implementation
  const renderVictoryChart = () => {
    const victoryData = transformedData.map((item) => ({
      x: item.name,
      y: item.value,
      label: `${item.name}: ${item.value}`
    }));

    const theme = VictoryTheme.material;

    switch (config.type) {
      case 'bar':
        return (
          <VictoryChart
            theme={theme}
            domainPadding={20}
            padding={{ left: 80, top: 40, right: 40, bottom: 80 }}
            animate={animated ? { duration: 1000, onLoad: { duration: 500 } } : false}
          >
            <VictoryAxis dependentAxis tickFormat={(x) => x.toLocaleString()} />
            <VictoryAxis fixLabelOverlap={true} />
            <VictoryBar
              data={victoryData}
              colorScale={colors}
              labelComponent={<VictoryTooltip />}
            />
          </VictoryChart>
        );

      case 'line':
        return (
          <VictoryChart
            theme={theme}
            padding={{ left: 80, top: 40, right: 40, bottom: 80 }}
            animate={animated ? { duration: 1000 } : false}
          >
            <VictoryAxis dependentAxis tickFormat={(x) => x.toLocaleString()} />
            <VictoryAxis fixLabelOverlap={true} />
            <VictoryLine
              data={victoryData}
              style={{
                data: { stroke: colors[0], strokeWidth: 3 }
              }}
              labelComponent={<VictoryTooltip />}
            />
          </VictoryChart>
        );

      case 'area':
        return (
          <VictoryChart
            theme={theme}
            padding={{ left: 80, top: 40, right: 40, bottom: 80 }}
            animate={animated ? { duration: 1000 } : false}
          >
            <VictoryAxis dependentAxis tickFormat={(x) => x.toLocaleString()} />
            <VictoryAxis fixLabelOverlap={true} />
            <VictoryArea
              data={victoryData}
              style={{
                data: { fill: colors[0], fillOpacity: 0.6, stroke: colors[0], strokeWidth: 2 }
              }}
              labelComponent={<VictoryTooltip />}
            />
          </VictoryChart>
        );

      case 'pie':
        return (
          <VictoryPie
            data={victoryData}
            x="x"
            y="y"
            colorScale={colors}
            labelRadius={({ innerRadius }) => innerRadius as number + 60 }
            animate={animated ? { duration: 1000 } : false}
            labelComponent={<VictoryTooltip />}
          />
        );

      case 'scatter':
        return (
          <VictoryChart
            theme={theme}
            padding={{ left: 80, top: 40, right: 40, bottom: 80 }}
            animate={animated ? { duration: 1000 } : false}
          >
            <VictoryAxis dependentAxis tickFormat={(x) => x.toLocaleString()} />
            <VictoryAxis fixLabelOverlap={true} />
            <VictoryScatter
              data={victoryData}
              size={7}
              colorScale={colors}
              labelComponent={<VictoryTooltip />}
            />
          </VictoryChart>
        );

      default:
        return (
          <VictoryChart
            theme={theme}
            domainPadding={20}
            padding={{ left: 80, top: 40, right: 40, bottom: 80 }}
            animate={animated ? { duration: 1000, onLoad: { duration: 500 } } : false}
          >
            <VictoryAxis dependentAxis tickFormat={(x) => x.toLocaleString()} />
            <VictoryAxis fixLabelOverlap={true} />
            <VictoryBar
              data={victoryData}
              colorScale={colors}
              labelComponent={<VictoryTooltip />}
            />
          </VictoryChart>
        );
    }
  };

  // Get chart icon
  const getChartIcon = () => {
    switch (config.type) {
      case 'bar': return <BarChart3 className="h-5 w-5" />;
      case 'pie': return <PieChartIcon className="h-5 w-5" />;
      case 'line': return <TrendingUp className="h-5 w-5" />;
      case 'area': return <Activity className="h-5 w-5" />;
      case 'scatter': return <Zap className="h-5 w-5" />;
      default: return <BarChart3 className="h-5 w-5" />;
    }
  };

  // Generate sample data if no data is available
  if (!config.data || config.data.length === 0) {
    const sampleData = [
      { label: 'Sample A', value: 45, count: 45 },
      { label: 'Sample B', value: 32, count: 32 },
      { label: 'Sample C', value: 28, count: 28 },
      { label: 'Sample D', value: 15, count: 15 },
      { label: 'Sample E', value: 8, count: 8 }
    ];
    config = { ...config, data: sampleData };
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
              {getChartIcon()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{config.title}</h3>
              <p className="text-sm text-gray-500 capitalize">{config.type} chart • {library}</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <p className="text-gray-500">Total</p>
              <p className="font-semibold text-gray-800">{stats.total.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500">Avg</p>
              <p className="font-semibold text-gray-800">{Math.round(stats.avg).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        <div className="h-80">
          {library === 'victory' ? renderVictoryChart() : renderRechartsChart()}
        </div>
      </div>

      {/* Footer with detailed stats */}
      <div className="px-6 pb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <p className="text-gray-500 mb-1">Records</p>
              <p className="font-semibold text-gray-800">{stats.count}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500 mb-1">Maximum</p>
              <p className="font-semibold text-green-600">{stats.max.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500 mb-1">Minimum</p>
              <p className="font-semibold text-orange-600">{stats.min.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500 mb-1">Range</p>
              <p className="font-semibold text-purple-600">{(stats.max - stats.min).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};